import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ArrowRight,
  BookOpen,
  TrendingUp,
  FileText,
  Shield,
  PieChart,
  Activity,
  BarChart3,
  Gauge,
  Database,
  Bot,
  Landmark,
  BookMarked,
} from 'lucide-react';
import { MODULE_LIST } from '../theme/moduleColors';

const ICON_MAP: Record<string, React.ReactElement> = {
  BookOpen: <BookOpen size={22} />,
  TrendingUp: <TrendingUp size={22} />,
  FileText: <FileText size={22} />,
  Shield: <Shield size={22} />,
  PieChart: <PieChart size={22} />,
  Bot: <Bot size={22} />,
  Landmark: <Landmark size={22} />,
  BookMarked: <BookMarked size={22} />,
};

interface KpiSpec {
  label: string;
  value: string;
  icon: React.ReactElement;
}

const KPI_CARDS: KpiSpec[] = [
  { label: 'Instruments',     value: '--', icon: <FileText size={18} /> },
  { label: 'Active Trades',   value: '--', icon: <Activity size={18} /> },
  { label: 'Pricing Runs',    value: '--', icon: <Gauge size={18} /> },
  { label: 'Market Data Sets', value: '--', icon: <Database size={18} /> },
];

const GlobalHome: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
          QFinPricer
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          Quantitative finance pricing and risk analytics platform.
        </Typography>
      </Box>

      {/* KPI Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {KPI_CARDS.map((kpi) => (
          <Grid size={{ xs: 6, sm: 3 }} key={kpi.label}>
            <Card variant="outlined">
              <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'text.secondary',
                      fontWeight: 600,
                      letterSpacing: '0.05em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {kpi.label}
                  </Typography>
                  <Box sx={{ color: theme.palette.text.secondary, display: 'flex' }}>
                    {kpi.icon}
                  </Box>
                </Box>
                <Typography
                  variant="h4"
                  fontWeight={700}
                  sx={{
                    color: 'text.primary',
                    fontFamily: '"JetBrains Mono", monospace',
                    fontSize: '1.5rem',
                  }}
                >
                  {kpi.value}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Status banner */}
      <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <BarChart3 size={18} color={theme.palette.primary.main} />
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Market data health, pricing engine status, and analytics overview will appear here once services are running.
          </Typography>
        </Box>
      </Card>

      {/* Module grid */}
      <Typography
        variant="overline"
        sx={{
          color: 'text.secondary',
          fontWeight: 600,
          letterSpacing: '0.08em',
        }}
      >
        Modules
      </Typography>
      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        {MODULE_LIST.map((mod) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={mod.id}>
            <Card
              variant="outlined"
              sx={{
                height: '100%',
                borderLeft: `3px solid ${mod.accent}`,
                transition: 'all 0.15s ease',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  boxShadow: '0 2px 8px rgba(15, 23, 33, 0.06)',
                },
              }}
            >
              <CardActionArea onClick={() => navigate(`/${mod.id}`)} sx={{ height: '100%' }}>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Box
                      sx={{
                        p: 1,
                        borderRadius: 0.5,
                        bgcolor: theme.palette.action.hover,
                        color: mod.accent,
                        display: 'flex',
                      }}
                    >
                      {ICON_MAP[mod.icon] || <BookOpen size={22} />}
                    </Box>
                    <ArrowRight size={18} color={theme.palette.text.disabled} />
                  </Box>
                  <Typography variant="h6" sx={{ mt: 2, mb: 0.5, color: 'text.primary' }}>
                    {mod.label}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {mod.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default GlobalHome;
